import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { TeamPlacement } from "@/components/people/TeamPlacement";
import { GUEST, PARTNER, UNIT, toBody } from "@/components/people/roles";

const FOLLOWS = "follows";

const Text = ({ label, value, onChange, testId, type = "text" }) => (
  <label className="block">
    <span className="label-caps">{label}</span>
    <Input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 h-10 rounded-none"
      data-testid={testId}
    />
  </label>
);

const start = (person, fallback) =>
  person
    ? { ...toBody(person), units: person.units.filter((u) => u !== person.home),
        access_role: person.access_override || person.team === GUEST ? person.access : "" }
    : { ...toBody({ team: fallback.team || UNIT, team_role: "", home: fallback.home || "" }), access_role: "" };

/** Add or update one person — their team role decides what they may do on the portal. */
export const MemberDialog = ({ open, onOpenChange, person, fallback = {}, data, onSaved }) => {
  const [form, setForm] = useState(() => start(person, fallback));
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(start(person, fallback));
      setInvite(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const teams = data.can_edit_all ? Object.keys(data.catalog) : [UNIT];
  const partner = form.team === PARTNER;

  const send = async (extra = {}) => {
    const body = { ...form, access_role: form.access_role || null, invite, ...extra };
    try {
      return (await api.put("/people/member", body)).data;
    } catch (e) {
      if (e?.response?.status === 409 && window.confirm(apiError(e))) {
        return (await api.put("/people/member", { ...body, acknowledge_orphans: true })).data;
      }
      throw e;
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.team_role) return toast.error("Name and the role in the team are needed.");
    setBusy(true);
    try {
      const res = await send();
      if (!res) return;
      toast.success(
        res.awaiting_verification
          ? `Sent to the unit SPOC for approval — ${form.name} changes once they clear it.`
          : res.invited
            ? `${form.name} saved and invited — the passcode is in their mail.`
            : `${form.name} saved.`,
      );
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Take ${form.name} off the team? Their portal access is switched off too.`)) return;
    setBusy(true);
    try {
      const res = await send({ remove: true });
      toast.success(res?.awaiting_verification ? "Sent to the unit SPOC for approval." : `${form.name} removed.`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black">
            {person ? `Update ${person.name}` : "Add a person"}
          </DialogTitle>
          <DialogDescription>
            Pick the team and the role — what they may do on the portal follows the role.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Text label="Name *" value={form.name} onChange={set("name")} testId="member-name" />
            <Text label="E-mail" value={form.email} onChange={set("email")} testId="member-email" type="email" />
          </div>
          <TeamPlacement
            catalog={data.catalog}
            plants={data.plants}
            teams={teams}
            guestAccess={data.guest_access}
            value={form}
            onChange={(v) => setForm((f) => ({ ...f, ...v }))}
            testPrefix="member"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Text label="Designation" value={form.designation} onChange={set("designation")} testId="member-designation" />
            <Text label="Department" value={form.department} onChange={set("department")} testId="member-department" />
            <Text label="Mobile" value={form.contact_number} onChange={set("contact_number")} testId="member-mobile" />
            <Text label="WhatsApp" value={form.whatsapp_number} onChange={set("whatsapp_number")} testId="member-whatsapp" />
            {partner ? (
              <Text label="Organisation" value={form.organisation} onChange={set("organisation")} testId="member-organisation" />
            ) : (
              <Text label="SAIL number" value={form.sail_id} onChange={set("sail_id")} testId="member-sail-id" />
            )}
          </div>
          {data.can_set_access && form.team !== GUEST && (
            <label className="block">
              <span className="label-caps">Access on the portal (super-admin)</span>
              <Select value={form.access_role || FOLLOWS} onValueChange={(v) => set("access_role")(v === FOLLOWS ? "" : v)}>
                <SelectTrigger className="mt-1 h-10 rounded-none" data-testid="member-access">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  <SelectItem value={FOLLOWS}>Follows the team role</SelectItem>
                  {data.access_roles.map((r) => (
                    <SelectItem key={r.key} value={r.key} data-testid={`member-access-option-${r.key}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
          {data.can_edit_all && !person?.account_id && form.email && (
            <label className="flex items-center gap-2 text-sm" data-testid="member-invite-row">
              <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} data-testid="member-invite" />
              Invite them to the portal now — a sign-in passcode is mailed to them
            </label>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          {person ? (
            <Button variant="outline" onClick={remove} disabled={busy} data-testid="member-remove" className="rounded-none text-destructive">
              <Trash2 className="mr-2 h-4 w-4" /> Take off the team
            </Button>
          ) : <span />}
          <Button onClick={save} disabled={busy} data-testid="member-save" className="rounded-none font-display text-xs font-bold uppercase tracking-widest">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
